/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useLockBodyScroll } from "react-use";
import { Button, Sidebar, Spacer, theme } from "@nordcloud/gnui";
import { FiltersFooter } from "~/components";
import { useQueryState } from "~/hooks";
import { FiltersProp } from "~/models/estateRecordRow";
import { getTypedKeys, isEmptyObj } from "~/tools";
import {
  CostAllocationFilters,
  CostAndSavingsFilters,
  ExternalIntegrationsFilters,
  FiltersByResourceAttribute,
} from "./Filters";

type Props = {
  isSidebarOpen: boolean;
  filters: FiltersProp;
  toggleIsSidebarOpen: (nextValue?: boolean) => void;
  updateFilters: (filter: FiltersProp) => void;
  toggleFilters: (openFilters: boolean) => void;
  clearFilters: () => void;
};

export function FiltersSidebar({
  isSidebarOpen,
  filters,
  toggleIsSidebarOpen,
  updateFilters,
  toggleFilters,
  clearFilters,
}: Props) {
  const { updateQueryState, removeFields } = useQueryState();

  const hasEmptyFilters = isEmptyObj(filters);

  const commit = () => {
    updateQueryState({ ...filters, page: 0 });
    toggleIsSidebarOpen(false);
  };

  const reset = () => {
    removeFields([...getTypedKeys(filters), "page"]);
    toggleIsSidebarOpen(false);
    clearFilters();
  };

  useLockBodyScroll(isSidebarOpen);

  return (
    <Sidebar
      title="Filters"
      isOpen={isSidebarOpen}
      width="37rem"
      onClick={() => toggleFilters(false)}
    >
      <CostAndSavingsFilters filters={filters} updateFilters={updateFilters} />
      <CostAllocationFilters filters={filters} updateFilters={updateFilters} />
      <FiltersByResourceAttribute
        filters={filters}
        updateFilters={updateFilters}
      />
      <ExternalIntegrationsFilters
        filters={filters}
        updateFilters={updateFilters}
      />
      <Spacer
        height={theme.spacing.spacing04}
        css={{ marginTop: theme.spacing.spacing04 }}
      />
      <FiltersFooter>
        <Button
          disabled={hasEmptyFilters}
          mr={theme.spacing.spacing02}
          onClick={commit}
        >
          Apply filters
        </Button>
        <Button severity="low" disabled={hasEmptyFilters} onClick={reset}>
          Clear filters &amp; reload
        </Button>
      </FiltersFooter>
    </Sidebar>
  );
}
